import React, { useState, useEffect, useMemo, useRef, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faTimes, faExternalLinkAlt, faChevronLeft, faChevronRight, faTrash } from '@fortawesome/free-solid-svg-icons';
import { DocumentService, sharedApiClient } from '../fetcher';
import { getAssigneeName as resolveAssigneeName, outOfOfficeForAssignee, StatusBadge, useAuth, useDocumentViewers, UserAbility } from '@medprompt/shared';
import { StatusEnum } from '../enums/statusEnum';
import PDFJSExpress from '@pdftron/pdfjs-express-viewer';
import '../detail/DocumentDetailView.css';

// Memoized PDF Viewer component to prevent unnecessary re-renders
const PDFViewer = memo(({ documentBlob, fileType, onReady, label, initDelay = 0 }) => {
  const viewerRef = useRef(null);
  const pdfInstanceRef = useRef(null);
  const blobUrlRef = useRef(null);
  const documentLoadedHandlerRef = useRef(null);
  const copyHandlerRef = useRef(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!documentBlob || !viewerRef.current) {
      setReady(false);
      return;
    }

    // Set not ready while we initialize
    setReady(false);

    const initializePDF = async () => {
      try {
        console.log(`[PDFViewer] Starting initialization for ${label}, blob size: ${documentBlob?.size}, type: ${documentBlob?.type}`);
        
        // Clean up existing instance first
        if (pdfInstanceRef.current) {
          console.log(`[PDFViewer] Cleaning up existing PDF instance for ${label}`);
          try {
            if (documentLoadedHandlerRef.current && pdfInstanceRef.current?.Core?.documentViewer) {
              pdfInstanceRef.current.Core.documentViewer.removeEventListener('documentLoaded', documentLoadedHandlerRef.current);
              documentLoadedHandlerRef.current = null;
            }
            
            await new Promise((resolve) => {
              pdfInstanceRef.current.dispose?.();
              pdfInstanceRef.current = null;
              setTimeout(resolve, 100);
            });
          } catch (error) {
            console.warn('Error disposing PDF instance:', error);
          }
        }
        
        // Revoke any existing blob URL
        if (blobUrlRef.current) {
          console.log(`[PDFViewer] Revoking previous blob URL for ${label}`);
          URL.revokeObjectURL(blobUrlRef.current);
          blobUrlRef.current = null;
        }

        // Check if we still want to initialize (component not unmounted, props not changed)
        if (!documentBlob || !viewerRef.current) {
          console.log(`[PDFViewer] Blob or viewer no longer available for ${label}, aborting`);
          return;
        }
        
        // Optional delay to stagger multiple viewer initializations
        if (initDelay > 0) {
          await new Promise(resolve => setTimeout(resolve, initDelay));
        }
        
        // Re-check after delay
        if (!documentBlob || !viewerRef.current) {
          console.log(`[PDFViewer] Blob or viewer no longer available after delay for ${label}, aborting`);
          return;
        }

        console.log(`[PDFViewer] Initializing PDF.js Express for ${label}`);
        // Initialize PDF.js Express
        const instance = await PDFJSExpress({
          path: '/pdfjsexpress',
          licenseKey: process.env.REACT_APP_PDF_VIEWER_KEY,
        }, viewerRef.current);

        // Check if we still want this instance
        if (!documentBlob || !viewerRef.current) {
          console.log(`[PDFViewer] Props changed during initialization for ${label}, disposing`);
          instance.dispose?.();
          return;
        }

        pdfInstanceRef.current = instance;
        console.log(`[PDFViewer] PDF.js Express initialized for ${label}`);
        
        instance.Core.disableEmbeddedJavaScript();

        // Intercept copy events so that OCR-layered newlines become spaces
        // rather than landing in the clipboard as individual lines.
        const iframeWindow = instance.UI.iframeWindow;
        if (iframeWindow) {
          copyHandlerRef.current = (e) => {
            const selectedText = instance.Core.documentViewer.getSelectedText?.();
            if (selectedText) {
              const normalized = selectedText.replace(/\n/g, ' ').replace(/ {2,}/g, ' ').trim();
              e.clipboardData.setData('text/plain', normalized);
              e.preventDefault();
            }
          };
          iframeWindow.addEventListener('copy', copyHandlerRef.current);
        }
        
        // Load document
        const blobUrl = URL.createObjectURL(documentBlob);
        blobUrlRef.current = blobUrl;
        console.log(`[PDFViewer] Loading document for ${label}, blob URL: ${blobUrl}, extension: ${fileType?.toLowerCase() || 'pdf'}`);
        
        instance.Core.documentViewer.loadDocument(blobUrl, {
          extension: fileType?.toLowerCase() || 'pdf'
        });

        // Set default zoom using median page width across all pages (capped at 200%).
        // FitWidth on page 1 alone can produce extreme zoom when the first page is
        // a tiny embedded image or narrow cover — majority voting avoids that.
        documentLoadedHandlerRef.current = () => {
          console.log(`[PDFViewer] Document loaded successfully for ${label}`);
          try {
            const documentViewer = instance.Core.documentViewer;
            const pdfDoc = documentViewer.getDocument();

            instance.UI.setFitMode(instance.UI.FitMode.FitWidth);
            const page1Zoom = documentViewer.getZoom();
            const page1Width = pdfDoc.getPageInfo(1)?.width;

            if (page1Width && page1Zoom) {
              const pageCount = documentViewer.getPageCount();

              const perPageZooms = [];
              for (let i = 1; i <= pageCount; i++) {
                const info = pdfDoc.getPageInfo(i);
                if (info?.width) {
                  perPageZooms.push(page1Zoom * (page1Width / info.width));
                }
              }

              perPageZooms.sort((a, b) => a - b);
              const medianZoom = perPageZooms[Math.floor(perPageZooms.length / 2)];

              const MAX_ZOOM = 2.0;
              const finalZoom = Math.min(medianZoom, MAX_ZOOM);

              console.log(`[PDFViewer] ${label} zoom: page1=${(page1Zoom * 100).toFixed(0)}%, median=${(medianZoom * 100).toFixed(0)}%, final=${(finalZoom * 100).toFixed(0)}%`);

              if (Math.abs(finalZoom - page1Zoom) > 0.05) {
                instance.UI.setFitMode(instance.UI.FitMode.Zoom);
                documentViewer.zoomTo(finalZoom);
              }
            }
          } catch (error) {
            console.warn(`[PDFViewer] Failed to set PDF zoom for ${label}:`, error);
          }
          setReady(true);
          if (onReady) onReady();
        };
        instance.Core.documentViewer.addEventListener('documentLoaded', documentLoadedHandlerRef.current);

      } catch (error) {
        console.error(`[PDFViewer] Error initializing PDF viewer for ${label}:`, error);
        setReady(false);
      }
    };

    initializePDF();

    // Cleanup function
    return () => {
      console.log(`[PDFViewer] Cleanup triggered for ${label}`);
      
      // Remove event listener
      if (documentLoadedHandlerRef.current && pdfInstanceRef.current?.Core?.documentViewer) {
        try {
          pdfInstanceRef.current.Core.documentViewer.removeEventListener('documentLoaded', documentLoadedHandlerRef.current);
        } catch (error) {
          console.warn('Error removing event listener:', error);
        }
        documentLoadedHandlerRef.current = null;
      }
      
      if (copyHandlerRef.current && pdfInstanceRef.current?.UI?.iframeWindow) {
        try {
          pdfInstanceRef.current.UI.iframeWindow.removeEventListener('copy', copyHandlerRef.current);
        } catch (error) {
          console.warn('Error removing copy event listener:', error);
        }
        copyHandlerRef.current = null;
      }

      // Dispose PDF instance
      if (pdfInstanceRef.current) {
        console.log(`[PDFViewer] Disposing PDF instance for ${label}`);
        try {
          pdfInstanceRef.current.dispose?.();
        } catch (error) {
          console.warn('Error disposing PDF viewer:', error);
        }
        pdfInstanceRef.current = null;
      }
      
      // Revoke blob URL to free memory
      if (blobUrlRef.current) {
        console.log(`[PDFViewer] Revoking blob URL for ${label}`);
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
      
      // Clear the container to help garbage collection
      if (viewerRef.current) {
        try {
          console.log(`[PDFViewer] Clearing viewer container for ${label}`);
          viewerRef.current.innerHTML = '';
        } catch (error) {
          console.warn('Error clearing viewer container:', error);
        }
      }
      
      setReady(false);
    };
  }, [documentBlob, fileType, onReady, initDelay, label]);

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      {!ready && (
        <div style={{ 
          position: 'absolute', 
          top: '50%', 
          left: '50%', 
          transform: 'translate(-50%, -50%)',
          zIndex: 1,
          textAlign: 'center'
        }}>
          <FontAwesomeIcon icon={faSpinner} spin />
          <div style={{ marginTop: '8px', fontSize: '12px' }}>Loading {label}...</div>
        </div>
      )}
      <div 
        ref={viewerRef}
        style={{ 
          width: '100%', 
          height: '100%',
          opacity: ready ? 1 : 0,
          transition: 'opacity 0.3s ease'
        }}
      />
    </div>
  );
}, (prevProps, nextProps) => {
  // Only re-render if documentBlob, fileType, or initDelay changes
  return prevProps.documentBlob === nextProps.documentBlob && 
         prevProps.fileType === nextProps.fileType &&
         prevProps.initDelay === nextProps.initDelay;
});

PDFViewer.displayName = 'PDFViewer';

// Same display rules as DocumentToolbar: a stored READY document is shown as
// In Review, Out of office, Assigned, or Ready for Review.
const resolveDocumentDisplayStatus = (doc, hasViewers, activeUsers) => {
  if (!doc?.status) return null;
  if (doc.status === StatusEnum.READY.value) {
    if (hasViewers?.(doc.documentId)) {
      return StatusEnum.IN_REVIEW;
    }
    if (outOfOfficeForAssignee(doc.assigneeId, activeUsers)) {
      return StatusEnum.ASSIGNED_TO_OOO;
    }
    if (doc.assigneeId) {
      return StatusEnum.ASSIGNED;
    }
    return StatusEnum.READY;
  }
  return StatusEnum[doc.status] || null;
};

const DocumentStatusBadge = ({ doc, hasViewers, activeUsers }) => {
  if (!doc?.status) return null;
  const displayStatus = resolveDocumentDisplayStatus(doc, hasViewers, activeUsers);
  return (
    <StatusBadge
      label={displayStatus?.description || doc.status}
      colorFamily={displayStatus?.colorFamily || 'gray'}
      size="normal"
    />
  );
};

const canDeleteDocument = (doc) => doc?.status === StatusEnum.READY.value;

const DuplicateDocumentSummary = ({
  label,
  labelColor,
  doc,
  assigneeName,
  hasViewers,
  activeUsers,
  showDelete,
  onDelete,
  confirming,
  isDeleting,
  onCancelConfirm,
  onConfirm,
}) => {
  const deleteEnabled = canDeleteDocument(doc);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px' }}>
        <div style={{ fontSize: '14px', fontWeight: 'bold', color: labelColor, marginBottom: 'auto' }}>
          {label}
        </div>
        {doc?.status && (
          <DocumentStatusBadge doc={doc} hasViewers={hasViewers} activeUsers={activeUsers} />
        )}
      </div>
      {confirming ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '13px', color: '#c62828', fontWeight: '500' }}>
            ⚠ Permanently delete this document?
          </span>
          <div style={{ display: 'flex', gap: '6px', marginLeft: 'auto' }}>
            <button
              onClick={onCancelConfirm}
              disabled={isDeleting}
              className="header-button"
              style={{ padding: '3px 10px', fontSize: '12px' }}
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={isDeleting}
              className="document-delete-confirm-button"
              style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '4px 12px', fontSize: '12px' }}
            >
              {isDeleting ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faTrash} style={{ fontSize: '11px' }} />}
              {isDeleting ? 'Deleting…' : 'Yes, Delete'}
            </button>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: '13px' }}>
              <strong>Title:</strong> {doc?.documentTitle || 'Untitled'}
            </div>
            <div style={{ fontSize: '13px' }}>
              <strong>Assignee:</strong> {assigneeName}
            </div>
          </div>
          {showDelete && (
            <button
              onClick={onDelete}
              className="header-button header-button-delete"
              style={{ padding: '4px 12px', fontSize: '12px', flexShrink: 0, marginTop: 'auto', marginRight: 0 }}
              disabled={!deleteEnabled}
              title={deleteEnabled ? 'Delete document' : 'Only documents ready for review can be deleted'}
            >
              <FontAwesomeIcon icon={faTrash} style={{ fontSize: '11px' }} />
              Delete
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// Document Preview Modal component
const DocumentPreviewModal = ({ isOpen, onClose, documentId, documentIds, activeUsers, teams = [], taskId, viewMode, fromConsultationId, onDeleteOtherDoc }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { hasViewers } = useDocumentViewers();
  const hasDocsWrite = Boolean(user?.authorities?.includes(UserAbility.DOCS_WRITE.key));
  const [currentDocData, setCurrentDocData] = useState(null);
  const [currentDocBlob, setCurrentDocBlob] = useState(null);
  const [duplicateDocData, setDuplicateDocData] = useState(null);
  const [duplicateDocBlob, setDuplicateDocBlob] = useState(null);
  const [loading, setLoading] = useState(false);
  const [duplicateLoading, setDuplicateLoading] = useState(false);
  const [duplicateNotFound, setDuplicateNotFound] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [deleteConfirmTarget, setDeleteConfirmTarget] = useState(null); // null | 'current' | 'other'
  const [isDeleting, setIsDeleting] = useState(false);
  const documentManager = useMemo(() => new DocumentService(sharedApiClient), []);

  const isDuplicateMode = viewMode === 'DUPLICATES';
  
  // Get the duplicate document ID to display
  const duplicateDocumentId = useMemo(() => {
    if (isDuplicateMode && documentIds && documentIds.length > 0) {
      return documentIds[currentIndex];
    }
    return null;
  }, [isDuplicateMode, documentIds, currentIndex]);

  // Reset current index when modal opens or documentIds change
  useEffect(() => {
    if (isDuplicateMode) {
      setCurrentIndex(0);
    }
  }, [isOpen, documentIds, isDuplicateMode]);

  // Fetch current document data (only once when modal opens in duplicate mode, or always in normal mode)
  useEffect(() => {
    if (!isOpen || !documentId) {
      return;
    }

    // In duplicate mode, only fetch current doc once
    // In normal mode, fetch the document
    if (isDuplicateMode) {
      setLoading(true);
      
      const fetchCurrentDoc = async () => {
        try {
          const [docData, docBlob] = await Promise.all([
            documentManager.fetchDocumentData(documentId),
            documentManager.fetchDocumentFile(documentId)
          ]);
          
          setCurrentDocData(docData);
          setCurrentDocBlob(docBlob);
        } catch (error) {
          console.error('Error fetching current document data:', error);
        } finally {
          setLoading(false);
        }
      };

      fetchCurrentDoc();
    } else {
      // Normal mode - fetch the single document
      setLoading(true);
      
      const fetchDoc = async () => {
        try {
          const [docData, docBlob] = await Promise.all([
            documentManager.fetchDocumentData(documentId),
            documentManager.fetchDocumentFile(documentId)
          ]);
          
          setCurrentDocData(docData);
          setCurrentDocBlob(docBlob);
        } catch (error) {
          console.error('Error fetching document data:', error);
        } finally {
          setLoading(false);
        }
      };

      fetchDoc();
    }
  }, [isOpen, documentId, documentManager, isDuplicateMode]);

  // Fetch duplicate document data when index changes (only in duplicate mode)
  useEffect(() => {
    if (!isOpen || !isDuplicateMode || !duplicateDocumentId) {
      return;
    }

    setDuplicateLoading(true);
    setDuplicateNotFound(false);
    setDuplicateDocData(null);
    setDuplicateDocBlob(null);
    setDeleteConfirmTarget(null);
    
    const fetchDuplicateDoc = async () => {
      try {
        // Fetch data and file separately to better handle individual failures
        let docData = null;
        let docBlob = null;
        let dataFailed = false;
        let fileFailed = false;
        
        // Try to fetch document data
        try {
          docData = await documentManager.fetchDocumentData(duplicateDocumentId);
        } catch (dataError) {
          console.error('Error fetching duplicate document data:', dataError);
          dataFailed = true;
          // Check if it's a 400/404 error indicating the document was deleted
          if (dataError.status === 400 || dataError.status === 404) {
            setDuplicateNotFound(true);
            return; // No point trying to fetch the file if document data doesn't exist
          }
        }
        
        // Try to fetch document file (only if data fetch succeeded)
        if (!dataFailed) {
          try {
            docBlob = await documentManager.fetchDocumentFile(duplicateDocumentId);
          } catch (fileError) {
            console.error('Error fetching duplicate document file:', fileError);
            fileFailed = true;
            // Check if it's a 400/404 error indicating the file was deleted
            if (fileError.status === 400 || fileError.status === 404) {
              setDuplicateNotFound(true);
              return;
            }
          }
        }
        
        // Only set the data if we successfully fetched both
        if (!dataFailed && !fileFailed) {
          setDuplicateDocData(docData);
          setDuplicateDocBlob(docBlob);
          setDuplicateNotFound(false);
        }
      } catch (error) {
        // Catch any unexpected errors
        console.error('Unexpected error fetching duplicate document:', error);
      } finally {
        setDuplicateLoading(false);
      }
    };

    fetchDuplicateDoc();
  }, [isOpen, isDuplicateMode, duplicateDocumentId, documentManager]);

  const getAssigneeName = (assigneeId) => {
    if (!assigneeId || assigneeId === '*') return 'Unassigned';
    return resolveAssigneeName(assigneeId, activeUsers, teams);
  };

  // Handle full view navigation
  const handleFullView = () => {
    onClose(); // Close the modal first

    const targetDocumentId = isDuplicateMode ? duplicateDocumentId : documentId;

    // Build navigation state
    const navState = {};
    
    if (taskId) {
      navState.taskId = taskId;
    }
    
    if (fromConsultationId) {
      navState.fromConsultation = true;
      navState.consultationId = fromConsultationId;
    }

    // Navigate with state if we have any context
    if (Object.keys(navState).length > 0) {
      navigate(`/document-detail/${targetDocumentId}`, {
        state: navState
      });
    } else {
      navigate(`/document-detail/${targetDocumentId}`);
    }
  };

  // Navigation handlers for DUPLICATES view mode
  const handleNavigatePrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const handleNavigateNext = () => {
    if (documentIds && currentIndex < documentIds.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const canNavigatePrev = isDuplicateMode && currentIndex > 0;
  const canNavigateNext = isDuplicateMode && documentIds && currentIndex < documentIds.length - 1;

  // Handle delete confirmation for either panel
  const handleDeleteConfirm = async (target) => {
    if (!hasDocsWrite) return;
    const targetDoc = target === 'current' ? currentDocData : duplicateDocData;
    if (!canDeleteDocument(targetDoc)) return;
    const idToDelete = target === 'current' ? documentId : duplicateDocumentId;
    if (!idToDelete) return;

    setIsDeleting(true);
    try {
      await documentManager.deleteDocument(idToDelete);
      if (target === 'current') {
        // SSE in DocumentDetailViewController will navigate away; just close the modal
        onClose();
      } else {
        onDeleteOtherDoc?.(idToDelete);
        setDuplicateNotFound(true);
        setDuplicateDocData(null);
        setDuplicateDocBlob(null);
      }
    } catch (error) {
      console.error(`Error deleting ${target} document:`, error);
    } finally {
      setIsDeleting(false);
      setDeleteConfirmTarget(null);
    }
  };

  // Determine modal title based on view mode
  const getModalTitle = () => {
    if (isDuplicateMode) {
      return 'Potential Duplicate Documents Detected';
    }
    return 'Document Preview';
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" style={{ zIndex: 1001 }}>
      <div className="modal-container" style={{ 
        maxWidth: isDuplicateMode ? '95vw' : '90vw', 
        maxHeight: '90vh', 
        width: isDuplicateMode ? '1600px' : '1200px', 
        height: '800px' 
      }}>
        <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2>{getModalTitle()}</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {isDuplicateMode && documentIds && documentIds.length > 1 && (
              <>
                <button 
                  className="header-button nav-button"
                  onClick={handleNavigatePrev}
                  disabled={!canNavigatePrev}
                  title={canNavigatePrev ? "Previous duplicate" : "No previous duplicate"}
                >
                  <FontAwesomeIcon icon={faChevronLeft} /> Prev
                </button>
                <span className="nav-position" style={{ padding: '0 10px' }}>
                  {currentIndex + 1} / {documentIds.length}
                </span>
                <button 
                  className="header-button nav-button"
                  onClick={handleNavigateNext}
                  disabled={!canNavigateNext}
                  title={canNavigateNext ? "Next duplicate" : "No next duplicate"}
                >
                  Next <FontAwesomeIcon icon={faChevronRight} />
                </button>
              </>
            )}
            <button 
              className="patient-diary-toggle"
              onClick={handleFullView}
              title="Open in full view"
            >
              <FontAwesomeIcon icon={faExternalLinkAlt} />
              Full View
            </button>
            <button className="close-button" onClick={onClose}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        </div>
        
        <div className="modal-content" style={{ height: 'calc(100% - 120px)', display: 'flex', flexDirection: 'column' }}>
          {isDuplicateMode && (
            <div style={{ 
              marginBottom: '15px', 
              padding: '15px', 
              backgroundColor: '#fff3cd', 
              borderRadius: '4px',
              border: '1px solid #ffc107'
            }}>
              <strong style={{ color: '#856404' }}>⚠️ Warning:</strong>
              <span style={{ marginLeft: '8px', color: '#856404' }}>
                This document may be a duplicate. Please review {documentIds?.length > 1 ? 'these matched documents' : 'this matched document'} carefully.
              </span>
            </div>
          )}
          
          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
              <FontAwesomeIcon icon={faSpinner} spin size="2x" />
              <span style={{ marginLeft: '10px' }}>Loading document...</span>
            </div>
          ) : isDuplicateMode ? (
            // Duplicate mode: Show two PDF viewers side by side
            <div style={{ flex: 1, display: 'flex', gap: '15px', minHeight: '500px' }}>
              {/* Current Document Panel */}
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                <div style={{ 
                  marginBottom: '10px', 
                  padding: '10px', 
                  backgroundColor: '#e3f2fd', 
                  borderRadius: '4px',
                  border: '2px solid #2196f3'
                }}>
                  <DuplicateDocumentSummary
                    label="Current Document"
                    labelColor="#1976d2"
                    doc={currentDocData}
                    assigneeName={getAssigneeName(currentDocData?.assigneeId)}
                    hasViewers={hasViewers}
                    activeUsers={activeUsers}
                    showDelete={hasDocsWrite && deleteConfirmTarget !== 'current'}
                    onDelete={() => setDeleteConfirmTarget('current')}
                    confirming={deleteConfirmTarget === 'current'}
                    isDeleting={isDeleting}
                    onCancelConfirm={() => setDeleteConfirmTarget(null)}
                    onConfirm={() => handleDeleteConfirm('current')}
                  />
                </div>
                
                <div style={{ flex: 1, position: 'relative', border: '2px solid #2196f3', borderRadius: '4px' }}>
                  {currentDocBlob ? (
                    <PDFViewer 
                      key={`current-${documentId}`}
                      documentBlob={currentDocBlob}
                      fileType={currentDocData?.fileType}
                      label="current document"
                      initDelay={800}
                    />
                  ) : (
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'center', 
                      alignItems: 'center', 
                      height: '100%' 
                    }}>
                      <FontAwesomeIcon icon={faSpinner} spin />
                      <span style={{ marginLeft: '8px' }}>Loading current document...</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Duplicate Document Panel */}
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                <div style={{ 
                  marginBottom: '10px', 
                  padding: '10px', 
                  backgroundColor: '#ffebee', 
                  borderRadius: '4px',
                  border: '2px solid #f44336'
                }}>
                  <DuplicateDocumentSummary
                    label="Potential Duplicate Match"
                    labelColor="#c62828"
                    doc={duplicateDocData}
                    assigneeName={getAssigneeName(duplicateDocData?.assigneeId)}
                    hasViewers={hasViewers}
                    activeUsers={activeUsers}
                    showDelete={hasDocsWrite && deleteConfirmTarget !== 'other' && !duplicateNotFound && !duplicateLoading && !!duplicateDocData}
                    onDelete={() => setDeleteConfirmTarget('other')}
                    confirming={deleteConfirmTarget === 'other'}
                    isDeleting={isDeleting}
                    onCancelConfirm={() => setDeleteConfirmTarget(null)}
                    onConfirm={() => handleDeleteConfirm('other')}
                  />
                </div>
                
                <div style={{ flex: 1, position: 'relative', border: '2px solid #f44336', borderRadius: '4px' }}>
                  {duplicateLoading ? (
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'center', 
                      alignItems: 'center', 
                      height: '100%' 
                    }}>
                      <FontAwesomeIcon icon={faSpinner} spin />
                      <span style={{ marginLeft: '8px' }}>Loading duplicate...</span>
                    </div>
                  ) : duplicateNotFound ? (
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'center', 
                      alignItems: 'center', 
                      height: '100%',
                      padding: '20px'
                    }}>
                      <div style={{
                        backgroundColor: '#f5f5f5',
                        borderRadius: '8px',
                        padding: '20px',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        minHeight: '80px',
                        width: '100%',
                        maxWidth: '600px'
                      }}>
                        <div style={{
                          color: '#666',
                          fontSize: '16px',
                          textAlign: 'center',
                          fontStyle: 'italic'
                        }}>
                          Document was not found because it was filed away and then deleted from MedPrompt. Please check the patient's record to confirm duplicates.
                        </div>
                      </div>
                    </div>
                  ) : duplicateDocBlob ? (
                    <PDFViewer 
                      key={`duplicate-${duplicateDocumentId}`}
                      documentBlob={duplicateDocBlob}
                      fileType={duplicateDocData?.fileType}
                      label="duplicate document"
                      initDelay={100}
                    />
                  ) : (
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'center', 
                      alignItems: 'center', 
                      height: '100%' 
                    }}>
                      <p>Failed to load duplicate document</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : currentDocData ? (
            // Normal mode: Show single PDF viewer
            <>
              <div style={{ marginBottom: '15px', padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                <div style={{ marginBottom: '8px' }}>
                  <strong>Title:</strong> {currentDocData.documentTitle || 'Untitled'}
                </div>
                <div>
                  <strong>Assignee:</strong> {getAssigneeName(currentDocData.assigneeId)}
                </div>
              </div>
              
              <div style={{ flex: 1, position: 'relative', minHeight: '500px' }}>
                {currentDocBlob ? (
                  <PDFViewer 
                    key={`single-${documentId}`}
                    documentBlob={currentDocBlob}
                    fileType={currentDocData?.fileType}
                    label="document"
                  />
                ) : (
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center', 
                    height: '100%' 
                  }}>
                    <FontAwesomeIcon icon={faSpinner} spin />
                    <span style={{ marginLeft: '8px' }}>Loading document...</span>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <p>Failed to load document data</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DocumentPreviewModal;
